/**
 * Корень приложения: маршрутизация, каркас и сквозная аналитика.
 *
 * Выбран `HashRouter`, а не `BrowserRouter`: десктопный клиент упакован
 * в Tauri и отдаётся не с веб-сервера, поэтому путь после `#` не требует
 * серверной переадресации и не ломается при перезагрузке окна.
 *
 * Профиль заводится здесь один раз — до этого `userId` пуст, а все
 * запросы к API пропускаются.
 *
 * Вход обязателен: без аккаунта с почтой открыты только приветствие,
 * онбординг, вход и документы (`isPublicPath`); любой другой маршрут
 * уводит на вход. До входа ученик — гость: `userId` у него локальный, на
 * сервер приложение не ходит (кроме онбординга гостя), аккаунта в базе нет.
 *
 * Переход между экранами анимируется через `AnimatePresence`: старый
 * экран уходит вниз и гаснет, новый поднимается и проявляется. Это
 * делает смену маршрута читаемой — пользователь видит, что «переехал»,
 * а не что содержимое подменили. Ключ — путь, поэтому анимация играет
 * ровно при смене экрана, а не при каждом ре-рендере.
 *
 * Постоянный хром (шапка с навигацией и нижний таб-бар) рендерит
 * `AppLayout` — снаружи `AnimatePresence`, поэтому он не перемонтируется
 * при переходах. Учебные экраны (`isImmersivePath`) идут без хрома.
 *
 * Слушатели сессии живут здесь же, на уровне приложения: они не должны
 * перезаводиться при переходах, иначе `session_end` считался бы по
 * времени одного экрана, а не всей сессии.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, lazy, useEffect, useRef } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { isImmersivePath, isPublicPath } from './app/navigation';
import { AppLayout } from './components/layout/AppLayout';
import { TutorialGate } from './components/tutorial/TutorialGate';
import { useMantineThemeSync } from './hooks/useMantineThemeSync';
import { DashboardScreen } from './pages/DashboardScreen';
import { WelcomeScreen } from './pages/WelcomeScreen';

/*
 * Экраны грузятся по требованию: в стартовом бандле — только приветствие и
 * главная, с которых открывается приложение. Остальное (онбординг, карта,
 * урок, карман, чат, звонок, игра, профиль) — отдельными чанками.
 */
const OnboardingScreen = lazy(() => import('./pages/OnboardingScreen').then((module) => ({ default: module.OnboardingScreen })));
const AptitudeScreen = lazy(() => import('./pages/AptitudeScreen').then((module) => ({ default: module.AptitudeScreen })));
const MapScreen = lazy(() => import('./pages/MapScreen').then((module) => ({ default: module.MapScreen })));
const LessonScreen = lazy(() => import('./pages/LessonScreen').then((module) => ({ default: module.LessonScreen })));
const PocketScreen = lazy(() => import('./pages/PocketScreen').then((module) => ({ default: module.PocketScreen })));
const ChatScreen = lazy(() => import('./pages/ChatScreen').then((module) => ({ default: module.ChatScreen })));
const CallScreen = lazy(() => import('./pages/CallScreen').then((module) => ({ default: module.CallScreen })));
const WordGameScreen = lazy(() => import('./pages/WordGameScreen').then((module) => ({ default: module.WordGameScreen })));
const SnowballScreen = lazy(() => import('./pages/SnowballScreen').then((module) => ({ default: module.SnowballScreen })));
const LoginScreen = lazy(() => import('./pages/LoginScreen').then((module) => ({ default: module.LoginScreen })));
const ServicesScreen = lazy(() => import('./pages/ServicesScreen').then((module) => ({ default: module.ServicesScreen })));
const GuideScreen = lazy(() => import('./pages/GuideScreen').then((module) => ({ default: module.GuideScreen })));
const GuideTopicScreen = lazy(() => import('./pages/GuideTopicScreen').then((module) => ({ default: module.GuideTopicScreen })));
const ProfileScreen = lazy(() => import('./pages/ProfileScreen').then((module) => ({ default: module.ProfileScreen })));
const PlacementScreen = lazy(() => import('./pages/PlacementScreen').then((module) => ({ default: module.PlacementScreen })));
const PlansScreen = lazy(() => import('./pages/PlansScreen').then((module) => ({ default: module.PlansScreen })));
const PracticeScreen = lazy(() => import('./pages/PracticeScreen').then((module) => ({ default: module.PracticeScreen })));
const LegalScreen = lazy(() => import('./pages/LegalScreen').then((module) => ({ default: module.LegalScreen })));

/** Пока чанк экрана грузится: квокка думает (обычно доли секунды). */
function ScreenLoading() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-[50vh] w-full place-items-center" role="status" aria-label={t('common.loading')}>
      <span className="h-10 w-10 animate-spin rounded-full border-4 border-accent/25 border-t-accent" />
    </div>
  );
}
import { identifyUser, initAnalytics, track } from './services/posthogService';
import { initServerSpeech } from './services/serverSpeech';
import { MicPermissionModal } from './components/MicPermissionModal';
import { useLanguageSync } from './hooks/useLanguageSync';
import { useLearningStore } from './store/learningStore';
import { isSignedIn, useUserStore } from './store/userStore';
import { useTranslation } from 'react-i18next';

/** Каркас с маршрутами: знает текущий путь, поэтому живёт внутри роутера. */
function Shell() {
  const location = useLocation();
  const immersive = isImmersivePath(location.pathname);
  const activeLanguage = useLearningStore((state) => state.activeLanguage);
  const signedIn = useUserStore(isSignedIn);

  // Ворота: без входа — только публичные экраны. Прямая ссылка на урок,
  // карту, чат или карман (и протухший ключ устройства) ведёт на вход.
  if (!signedIn && !isPublicPath(location.pathname)) return <Navigate to="/login" replace state={{ from: 'gate' }} />;

  // Хром (`AppLayout`) лежит СНАРУЖИ `AnimatePresence`: шапка и таб-бар не
  // должны перемонтироваться и мигать при смене маршрута — анимируется
  // только содержимое экрана, ключом по пути. В ключе и изучаемый язык:
  // его смена перемонтирует экран, и он загружает данные нового языка.
  return (
    <AppLayout immersive={immersive}>
      {/* Тур по приложению — сразу после первого входа, на любом экране. */}
      <TutorialGate />
      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeLanguage}:${location.pathname}`}
          className="flex w-full min-w-0 justify-center"
          // Переход экранов: новый «выплывает» снизу из лёгкого размытия,
          // старый уходит вверх и тает. Без жёстких склеек.
          initial={{ opacity: 0, y: 24, scale: 0.985, filter: 'blur(6px)' }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, y: -16, scale: 0.99, filter: 'blur(4px)' }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          <Suspense fallback={<ScreenLoading />}>
          <Routes location={location}>
            <Route path="/" element={<WelcomeScreen />} />
            <Route path="/onboarding" element={<OnboardingScreen />} />
            <Route path="/aptitude" element={<AptitudeScreen />} />
            <Route path="/map" element={<MapScreen />} />
            <Route path="/lesson/:skillId" element={<LessonScreen />} />
            <Route path="/pocket" element={<PocketScreen />} />
            <Route path="/chat" element={<ChatScreen />} />
            <Route path="/call" element={<CallScreen />} />
            <Route path="/word-game" element={<WordGameScreen />} />
            <Route path="/snowball" element={<SnowballScreen />} />
            <Route path="/guide" element={<GuideScreen />} />
            <Route path="/services" element={<ServicesScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/guide/:topicId" element={<GuideTopicScreen />} />
            <Route path="/dashboard" element={<DashboardScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/placement" element={<PlacementScreen />} />
            <Route path="/plans" element={<PlansScreen />} />
            <Route path="/legal/:doc" element={<LegalScreen />} />
            <Route path="/practice/:kind" element={<PracticeScreen />} />
            <Route path="/progress" element={<Navigate to="/profile" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>
    </AppLayout>
  );
}

export function App() {
  const ensureUser = useUserStore((state) => state.ensureUser);
  const userId = useUserStore((state) => state.userId);
  const completedLessonIds = useUserStore((state) => state.completedLessonIds);
  const signedIn = useUserStore(isSignedIn);
  useMantineThemeSync();
  // Языки и речь — данные аккаунта: у гостя их на сервере нет.
  useLanguageSync(signedIn ? userId : '');

  // Последнее число пройденных уроков — для `session_end` на уход.
  const lessonsDoneRef = useRef(0);

  useEffect(() => {
    ensureUser();
  }, [ensureUser]);

  // Аналитика включается один раз; без ключа — тихо выключена.
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (userId.length === 0 || !signedIn) return;
    identifyUser(userId);
    // Своя серверная речь (если настроена) — узнаём один раз после входа.
    void initServerSpeech();
  }, [userId, signedIn]);

  // Разрешение на микрофон — один раз, на первом касании после входа: iOS
  // показывает запрос только по жесту, а до входа он ни к чему. Дальше
  // распознавание пользуется уже выданным разрешением.
  // Разрешение на микрофон спрашивается при первом прослушивании
  // (`services/micPermission.ts`), а не по любому касанию после входа.

  useEffect(() => {
    lessonsDoneRef.current = completedLessonIds.length;
  }, [completedLessonIds.length]);

  // Конец сессии: считаем по уходу со страницы или сворачиванию окна.
  useEffect(() => {
    let startedAt = Date.now();
    let ended = false;

    const endSession = () => {
      if (ended) return;
      ended = true;
      track('session_end', {
        duration: Date.now() - startedAt,
        lessonsDone: lessonsDoneRef.current,
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        endSession();
      } else {
        // Возврат к окну открывает новую сессию: отсчёт начинается заново.
        ended = false;
        startedAt = Date.now();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', endSession);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', endSession);
    };
  }, []);

  return (
    <HashRouter>
      <MicPermissionModal />
      <Shell />
    </HashRouter>
  );
}
